import JSEncrypt from 'jsencrypt';
import { authAPI } from '../services/api';

let cachedPublicKey: string | null = null;

/**
 * 从后端获取 RSA 公钥，缓存到内存
 */
export const getRSAPublicKey = async (): Promise<string | null> => {
  if (cachedPublicKey) {
    return cachedPublicKey;
  }

  try {
    const response = await authAPI.getPublicKey();
    if (response.data && response.data.public_key) {
      cachedPublicKey = response.data.public_key;
      return cachedPublicKey;
    }
    return null;
  } catch (error) {
    console.error('获取RSA公钥失败:', error);
    return null;
  }
};

/**
 * 使用 JSEncrypt 库进行 RSA 加密
 */
export const encryptWithRSA = (data: string, publicKey: string): string | false => {
  const encrypt = new JSEncrypt();
  encrypt.setPublicKey(publicKey);
  return encrypt.encrypt(data);
};

/**
 * 加密登录数据
 */
export const encryptLoginData = async (
  username: string,
  password: string
): Promise<{ username: string; password: string; encrypted: boolean }> => {
  const publicKey = await getRSAPublicKey();

  if (!publicKey) {
    // 获取公钥失败，降级为明文传输
    return { username, password, encrypted: false };
  }

  const encryptedUsername = encryptWithRSA(username, publicKey);
  const encryptedPassword = encryptWithRSA(password, publicKey);

  if (!encryptedUsername || !encryptedPassword) {
    // 加密失败，降级为明文传输
    return { username, password, encrypted: false };
  }

  return {
    username: encryptedUsername,
    password: encryptedPassword,
    encrypted: true,
  };
};

/**
 * 加密注册数据
 */
export const encryptRegisterData = async (
  username: string,
  email: string,
  password: string,
  invite_code?: string
): Promise<Record<string, any>> => {
  const publicKey = await getRSAPublicKey();

  if (!publicKey) {
    // 获取公钥失败，降级为明文传输
    const result: Record<string, any> = { username, email, password, encrypted: false };
    if (invite_code) result.invite_code = invite_code;
    return result;
  }

  const encryptedUsername = encryptWithRSA(username, publicKey);
  const encryptedEmail = encryptWithRSA(email, publicKey);
  const encryptedPassword = encryptWithRSA(password, publicKey);

  if (!encryptedUsername || !encryptedEmail || !encryptedPassword) {
    // 加密失败，降级为明文传输
    const result: Record<string, any> = { username, email, password, encrypted: false };
    if (invite_code) result.invite_code = invite_code;
    return result;
  }

  const result: Record<string, any> = {
    username: encryptedUsername,
    email: encryptedEmail,
    password: encryptedPassword,
    encrypted: true,
  };

  if (invite_code) {
    const encryptedInviteCode = encryptWithRSA(invite_code, publicKey);
    result.invite_code = encryptedInviteCode || invite_code;
  }

  return result;
};
